<template>
  <div v-if="!isPasswordless && !verified && !isLoggedIn" class="pb-24">
    <div
      v-if="isNewAuth"
      class="mt-10 mx-auto sp-announce-margin-top"
      style="max-width: 738px"
    >
      <AccountSharingAnnounce />
    </div>
    <div
      class="mx-auto"
      :class="[isNewAuth ? 'sp-login-form-margin-top' : 'mt-16']"
      style="max-width: 35rem"
    >
      <div class="bg-white py-8 sp-rounded-lg">
        <form @submit="onSubmit">
          <h2 class="sp-headline-s text-center text-2xl leading-8 pb-4">
            PRONIパートナーログイン
          </h2>
          <div class="pb-8">
            <Field
              v-slot="{ field, handleChange, errorMessage }"
              v-for="input in formInputs"
              :key="input.name"
              :name="input.name"
              :validateOnInput="true"
            >
              <BaseTextInput
                required
                :name="input.name"
                :type="input.type"
                :placeholder="input.placeholder"
                :icon="input.icon"
                :field="field"
                :error-message="errorMessage"
                :on-change="handleChange"
                class="w-10/12 m-auto mb-4 sp-body-l-regular"
              />
            </Field>

            <Field
              v-slot="{ field, handleChange }"
              type="checkbox"
              name="keepLogin"
              :value="true"
            >
              <BaseCheckbox
                name="keepLogin"
                :value="true"
                :field="field"
                :on-change="handleChange"
                class="flex w-10/12 mx-auto"
              >
                ログイン情報を保持する
              </BaseCheckbox>
            </Field>
          </div>
          <div class="text-center">
            <button
              type="submit"
              class="w-10/12 mb-4 h-10 text-white font-semibold rounded focus:outline-none bg-coral-main hover:button"
            >
              ログイン
            </button>
          </div>
          <div class="text-center">
            <router-link
              :to="{ name: 'PasswordResetRequest' }"
              class="w-10/12 inline-block text-gray-92 font-semibold h-10 py-2 box-border border border-gray-38 rounded hover:bg-gray-04 focus:bg-gray-12 active:bg-gray-10 focus:outline-none"
            >
              パスワードを忘れた方はこちら
            </router-link>
          </div>
        </form>
      </div>
      <ul class="flex justify-end leading-4 pt-4">
        <li class="px-2">
          <a
            href="https://buyer.imitsu-cloud.jp/login"
            class="text-xs text-gray-92"
            >カスタマーログイン</a
          >
        </li>
      </ul>
    </div>
  </div>
  <div v-else-if="verified">
    <div class="text-center mt-32">
      <div class="text-3xl mb-10">
        現在審査中です。審査完了までお待ちください。
      </div>
      <div class="leading-6">
        不明点があれば、右下のチャットから相談してください。
      </div>
      <div class="leading-6">
        <span
          @click="globalMethods.loginToBuyer()"
          class="cursor-pointer underline font-semibold"
          >カスタマーとしてご利用いただけます</span
        >
        。お待ちの間にご確認ください。
      </div>
    </div>
  </div>
</template>

<script>
import { reactive, onMounted, toRefs, inject } from 'vue';
import { useRouter } from 'vue-router';
import { Field, useForm } from 'vee-validate';
import { VueCookieNext } from 'vue-cookie-next';

import axios from '@/utils/axios.js';
import validateRules from '@/utils/validateRules.js';
import BaseTextInput from '@/components/form/BaseTextInput.vue';
import BaseCheckbox from '@/components/form/BaseCheckbox.vue';
import AccountSharingAnnounce from '@/views/login/AccountSharingAnnounce.vue';

export default {
  components: {
    Field,
    BaseTextInput,
    BaseCheckbox,
    AccountSharingAnnounce,
  },
  setup() {
    const router = useRouter();
    const globalMethods = inject('globalMethods');
    const query = useRouter().currentRoute.value.query;
    const isPasswordless = Boolean(query.otp_id) && Boolean(query.signature);

    const formInputs = [
      {
        name: 'email',
        placeholder: 'メールアドレス',
        type: 'email',
        icon: 'Mail',
      },
      {
        name: 'password',
        placeholder: 'パスワード',
        type: 'password',
        icon: 'Lock',
      },
    ];

    const state = reactive({
      accessToken: '',
      isPasswordless,
      verified: false,
      isLoggedIn: true,
    });

    const { handleSubmit } = useForm({
      validationSchema: {
        email(value) {
          return validateRules.validate(value, 'required|email');
        },
        password(value) {
          return validateRules.validate(value, 'required');
        },
      },
    });
    onMounted(async () => {
      const isPasswordlessLoginOK = await checkPasswordlessLogin();
      // 調査中の場合は、ログイン画面を表示しない
      if (isPasswordlessLoginOK === false) {
        return;
      }
      if (query.email_update_verify_completed) {
        if (VueCookieNext.getCookie('accessToken')) {
          await globalMethods.logout();
        }
        globalMethods.updateModal(
          true,
          '認証が完了しました',
          '新しいメールアドレスでログインしてください。'
        );
        state.isLoggedIn = false;
        return;
      }

      if (!VueCookieNext.getCookie('accessToken')) {
        state.isLoggedIn = false;
        if (query.email_verify_completed) {
          globalMethods.updateModal(
            true,
            'メール認証',
            'メール認証が完了しました。'
          );
        }
        return;
      }

      try {
        state.accessToken = VueCookieNext.getCookie('accessToken');
        const seller = await getSeller();

        if (!seller) {
          VueCookieNext.removeCookie('accessToken');
          globalMethods.updateModal(
            true,
            'ログインに失敗しました。',
            'PRONIアイミツメンバーズ に登録されていません。'
          );
          return;
        }

        loginCompleted(seller);
      } catch (e) {
        state.isLoggedIn = false;
        VueCookieNext.removeCookie('accessToken');
      }
    });

    const checkPasswordlessLogin = async () => {
      if (!state.isPasswordless) return;

      VueCookieNext.removeCookie('accessToken');

      return await axios
        .post('/auth/passwordless-login')
        .then(async (response) => {
          const { token_type, access_token, refresh_ttl } = response.data;
          const accessToken = `${token_type} ${access_token}`;
          VueCookieNext.setCookie('accessToken', accessToken, {
            expire: refresh_ttl,
          });
          const seller = await getSeller();
          return loginCompleted(seller);
        })
        .catch((e) => {
          if (e.response && e.response.status == 401) {
            state.isPasswordless = false;
            globalMethods.updateModal(
              true,
              'ログインに失敗しました。',
              'URLまたはメールアドレスとパスワードをご確認ください。'
            );
          }
          globalMethods.sendLog(e);
        });
    };

    const getSeller = async () => {
      return await axios
        .get('/sellers', { headers: { Authorization: state.accessToken } })
        .then((response) => response.data.data[0]);
    };

    const login = async (inputValues) => {
      await axios
        .post('/auth/login', inputValues)
        .then((response) => {
          const { token_type, access_token, refresh_ttl } = response.data;
          state.accessToken = `${token_type} ${access_token}`;
          const expire = inputValues.keepLogin ? refresh_ttl : 0;
          VueCookieNext.setCookie('accessToken', state.accessToken, { expire });
        })
        .catch((e) => {
          if (e.response.status == 401) {
            let errorMessage =
              e.response.data.error ||
              'メールアドレスとパスワードをご確認ください。';
            return globalMethods.updateModal(
              true,
              'ログインに失敗しました。',
              errorMessage
            );
          }
          globalMethods.updateModal(
            true,
            'リクエストが完了しませんでした。',
            ''
          );
          globalMethods.sendLog(e);
        });
    };

    const onSubmit = handleSubmit(async (inputValue) => {
      await login(inputValue);

      if (state.accessToken) {
        const seller = await getSeller();

        if (!seller) {
          VueCookieNext.removeCookie('accessToken');
          globalMethods.updateModal(
            true,
            'ログインに失敗しました。',
            'PRONIアイミツメンバーズ に登録されていません。'
          );
          return;
        }
        loginCompleted(seller);
      }
    });

    const loginCompleted = async (seller) => {
      if (!seller.company_id) {
        state.verified = true;
        return false;
      }

      const redirectPath = router.currentRoute.value.query.redirect;
      if (!redirectPath) {
        window.location = process.env.VUE_APP_NEW_NEXT_DOMAIN + '/tx';
      } else {
        window.location = redirectPath;
      }
    };

    const isNewAuth = process.env.VUE_APP_IS_NEW_AUTH === 'true';

    return {
      ...toRefs(state),
      formInputs,
      onSubmit,
      globalMethods,
      isNewAuth,
    };
  },
};
</script>

<style lang="scss" scoped>
.icon {
  position: relative;
  &:after {
    position: absolute;
    top: 14px;
    left: 14px;
    content: '';
    display: block;
    width: 15px;
    height: 15px;
    background-size: contain;
  }
  &-email {
    &:after {
      background-image: url('https://laravel.com/img/notification-logo.png');
    }
  }
  &-password {
    &:after {
      background-image: url('https://laravel.com/img/notification-logo.png');
    }
  }
}
input[type='checkbox'] {
  box-sizing: border-box;
  display: inline-block;
  position: relative;
  width: 14px;
  height: 14px;
  border: 1px solid transparent;
  border-radius: 3px;
  &::before {
    position: absolute;
    top: -1px;
    left: -1px;
    background: #fff;
    content: '';
    display: block;
    width: 16px;
    height: 16px;
    @apply border;
    border-radius: 3px;
  }
  &::after {
    position: absolute;
    top: 1px;
    left: 4.5px;
    border-right: 2px solid #fff;
    border-bottom: 2px solid #fff;
    content: '';
    display: block;
    width: 5px;
    height: 9px;
    opacity: 0;
    transform: rotate(48deg);
  }
  &:hover::before {
    @apply border border-gray-92;
    box-shadow: 0px 0px 0px 2px rgba(52, 68, 100, 0.1);
  }
  &:checked::before {
    background: #28395c;
    border: 1px solid #28395c;
  }
  &:checked::after {
    opacity: 1;
  }
}

.sp-headline-s {
  font-size: 24px;
  letter-spacing: 0.18px;
}
.sp-padding {
  padding-top: 32px;
  padding-bottom: 32px;
}
.sp-login-form-margin-top {
  margin-top: 24px;
}
.sp-rounded-lg {
  border-radius: 0.5rem;
}

@media (max-width: 640px) {
  .sp-body-l-regular {
    font-size: 16px;
    font-weight: 600;
    letter-spacing: 0.15px;
  }
  .sp-headline-s {
    font-size: 20px;
    line-height: 150%; /* 30px */
    letter-spacing: 0.15px;
  }
  .sp-padding {
    padding-top: 24px;
    padding-bottom: 24px;
  }
  .sp-announce-margin-top {
    margin-top: 16px;
  }
  .sp-login-form-margin-top {
    margin-top: 16px;
  }
  .sp-rounded-lg {
    border-radius: 0;
  }
}
</style>
